import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useHistory } from "react-router";
import { ScorePage } from "src/pages/authenticated/ScoreTemplates";
import { fontMedium, useFeatureFlags } from "swing-components";

import { ContentSingleColumn, useAuth } from "~components";
import { TaskCard } from "~onboarding/components";
import { computeGroupsState, getConfiguration, type TaskComputed } from "~onboarding/utils";
import { createMoreUrl, createPermitTaskURL } from "~pages";
import { GET, SubAttribute, translate } from "~utils";

/**
 * Page | Permit
 * URL: /more/permit
 *
 * Renders the SwingCert or the PermitAssistLA task based on the user's onboarding configuration.
 */
export function PermitPage() {
  const { userInfo } = useAuth();
  const { flags } = useFeatureFlags();
  const history = useHistory();

  useEffect(() => {
    if (!userInfo.canAccessPermitTask) {
      // eslint-disable-next-line no-console
      console.error(`Unauthorized access attempt to permit page.`);

      history.replace(createMoreUrl());
    }
  }, [history, userInfo.canAccessPermitTask]);

  /*** Queries ***/
  const { data: onboardingConfiguration, isError: isConfigurationError } = useQuery({
    queryKey: [
      "configuration",
      userInfo.onboardingConfigurationId,
      userInfo.onboardingConfigurationId === "ca-v4" ? flags?.onboardingTask : undefined,
    ],
    queryFn: () =>
      getConfiguration(userInfo.onboardingConfigurationId || "", {
        featureFlags: flags,
        userInfo: userInfo,
      }),
    enabled: Boolean(userInfo.onboardingConfigurationId && userInfo.canAccessPermitTask),
    staleTime: Infinity,
    retry: false,
  });

  const { data: subAttributes, isError: isSubAttributesError } = useQuery({
    queryKey: ["attributes"],
    queryFn: () => GET("/api/sub/attributes"),
    select: (data) =>
      data.data.map((attribute) => ({
        ...attribute,
        updatedAt: new Date(attribute.updatedAt),
      })) as Array<SubAttribute>,
    retryDelay: 500,
    enabled: Boolean(userInfo.canAccessPermitTask),
  });

  if (!userInfo.canAccessPermitTask) {
    return null;
  }

  if (isSubAttributesError || isConfigurationError) {
    return (
      <ScorePage title={translate("PERMIT_PAGE_TITLE")} hasBack>
        <ContentSingleColumn>
          <div css={{ padding: "24px 16px" }}>
            <p>Unable to load data. Please try again later.</p>
          </div>
        </ContentSingleColumn>
      </ScorePage>
    );
  }

  if (!subAttributes || !onboardingConfiguration) {
    return <ScorePage title={translate("PERMIT_PAGE_TITLE")} hasBack isLoading />;
  }

  const groupsComputed = computeGroupsState({
    groups: onboardingConfiguration.groups,
    syncedSubAttributes: subAttributes,
  });

  const permitTask: TaskComputed[] = groupsComputed
    .flatMap((group) => group.tasks)
    .filter((task) => task.id === "swing-assist" || task.id === "permit-assist-la");

  return (
    <ScorePage title={translate("PERMIT_PAGE_TITLE")} hasBack>
      <ContentSingleColumn>
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            paddingTop: "24px",
            paddingBottom: "24px",
            paddingLeft: "16px",
            paddingRight: "16px",
          }}
        >
          <p css={{ ...fontMedium(), margin: 0 }}>{translate("PERMIT_PAGE_CONTENT")}</p>
          <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {permitTask.length === 0 ? (
              <p css={{ textAlign: "center", color: "#666", padding: "24px" }}>
                No tasks available at this time.
              </p>
            ) : (
              permitTask.map((task, taskIndex) => (
                <TaskCard
                  key={task.name}
                  {...task}
                  onClick={() => {
                    // Navigate to the task with the first step (0)
                    history.push(createPermitTaskURL(taskIndex, 0));
                  }}
                />
              ))
            )}
          </div>
        </div>
      </ContentSingleColumn>
    </ScorePage>
  );
}
